import type { GolfFitAssessment, SchoolTier } from "./schoolFits";
export type { GolfFitAssessment, GolfFitEvidence, GolfFitStatus, SchoolTier } from "./schoolFits";

const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

export const PIPELINE_UPDATED_EVENT = "recruitai:pipeline-updated";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export interface PipelineSchool {
  schoolName: string;
  teamId: string | null;
  collegeId?: string | null;
  division: string | null;
  conference: string | null;
  state: string | null;
  city?: string | null;
  headCoach: string | null;
  headCoachEmail: string | null;
  gender: string | null;
  logoUrl?: string | null;
  brandColorPrimary?: string | null;
  brandColorSecondary?: string | null;
  nationalGolfRank?: number | null;
  golfRankingSeason?: string | null;
}

export type PipelineStage =
  | "researching"
  | "reached_out"
  | "in_conversation"
  | "visited"
  | "offered";

export interface PipelineEntry {
  _id: string;
  school: PipelineSchool;
  stage: PipelineStage;
  outreachId: string | null;
  outreachStatus: string | null;
  notes: string | null;
  addedBy: "ai" | "manual" | "outreach";
  recommendationContext?: "preference_match" | "perspective" | "exploration" | null;
  recommendationReason?: string | null;
  tier: SchoolTier | null;
  golfFit?: GolfFitAssessment | null;
  createdAt: string;
  updatedAt: string;
}

export interface PipelineListResponse {
  pipeline: PipelineEntry[];
  counts: Record<PipelineStage, number>;
  portfolioMode?: "matched" | "exploration";
  hasUsableGolfFitData?: boolean;
  missingGolfFitFields?: string[];
}

export async function fetchPipeline(params?: {
  linkedJgsProfile?: string;
}): Promise<PipelineListResponse> {
  const url = new URL(withBase("/pipeline"));
  if (params?.linkedJgsProfile) url.searchParams.set("linkedJgsProfile", params.linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch pipeline (${resp.status})`);
  }

  return resp.json();
}

export async function updatePipelineStage(
  id: string,
  stage: PipelineStage,
  linkedJgsProfile?: string
): Promise<PipelineEntry> {
  const url = new URL(withBase(`/pipeline/${encodeURIComponent(id)}/stage`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ stage }),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to update pipeline stage (${resp.status})`);
  }

  return resp.json();
}

export async function refreshPipelineGolfFit(
  id: string,
  linkedJgsProfile?: string
): Promise<PipelineEntry> {
  const url = new URL(withBase(`/pipeline/${encodeURIComponent(id)}/golf-fit`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "POST",
    credentials: "include",
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new Error(body?.error || `Failed to refresh golf fit (${resp.status})`);
  }

  return resp.json();
}

export interface GeneratePlanResult {
  generated: number;
  added: number;
  skipped: number;
  counts: { dream: number; realistic: number; safety: number };
  reason?: string;
  mode?: "matched" | "exploration";
  portfolioSize?: number;
  replaced?: number;
}

/** A school the onboarding wizard collected (dream pick or recruiting progress). */
export interface OnboardingSchoolInput {
  teamId: string;
  schoolName: string;
  division?: string | null;
  conference?: string | null;
  state?: string | null;
  headCoach?: string | null;
  headCoachEmail?: string | null;
  gender?: string | null;
}

export interface OnboardingProgressInput extends OnboardingSchoolInput {
  stage: PipelineStage;
}

export interface GenerateRecruitingPlanBody {
  dreamSchools?: OnboardingSchoolInput[];
  recruitingProgress?: OnboardingProgressInput[];
  refreshRecommendations?: boolean;
}

/**
 * Generate a starter recruiting plan and add it to My Schools.
 * Called when onboarding completes so the user lands in a populated pipeline.
 * Optionally seeds the plan with the wizard's dream picks and recruiting progress.
 */
export async function generateRecruitingPlan(
  body?: GenerateRecruitingPlanBody
): Promise<GeneratePlanResult> {
  const resp = await fetch(withBase("/pipeline/generate"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body ?? {}),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to generate recruiting plan (${resp.status})`);
  }

  const result = (await resp.json()) as GeneratePlanResult;
  window.dispatchEvent(new Event(PIPELINE_UPDATED_EVENT));
  return result;
}

export async function addToPipeline(
  school: {
    schoolName: string;
    teamId?: string | null;
    division?: string | null;
    conference?: string | null;
    state?: string | null;
    headCoach?: string | null;
    headCoachEmail?: string | null;
    gender?: string | null;
  },
  linkedJgsProfile?: string
): Promise<PipelineEntry> {
  const url = new URL(withBase("/pipeline"));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(school),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to add to pipeline (${resp.status})`);
  }

  return resp.json();
}

export async function updatePipelineTier(
  id: string,
  tier: SchoolTier,
  linkedJgsProfile?: string
): Promise<PipelineEntry> {
  const url = new URL(withBase(`/pipeline/${encodeURIComponent(id)}/tier`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ tier }),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to update tier (${resp.status})`);
  }

  return resp.json();
}

export async function updatePipelineNotes(
  id: string,
  notes: string,
  linkedJgsProfile?: string
): Promise<PipelineEntry> {
  const url = new URL(withBase(`/pipeline/${encodeURIComponent(id)}/notes`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ notes }),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to update notes (${resp.status})`);
  }

  return resp.json();
}

export async function reorderPipeline(
  entryIds: string[],
  linkedJgsProfile?: string
): Promise<void> {
  const url = new URL(withBase("/pipeline/reorder"));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ entryIds }),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to reorder pipeline (${resp.status})`);
  }
}

export async function removePipelineEntry(id: string, linkedJgsProfile?: string): Promise<void> {
  const url = new URL(withBase(`/pipeline/${encodeURIComponent(id)}`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "DELETE",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to remove pipeline entry (${resp.status})`);
  }
}
